import { checkCandidate, type IsNameTaken } from './candidate.js'

/**
 * The longest a Sprint name may be. Generated names are two words and come
 * nowhere near it; the bound exists for the free-text rename path, and lives
 * here rather than in the tRPC input schema so every caller of
 * {@link validateSprintName} -- a script, a second router, LG-1004's UI
 * wanting a `maxLength` hint -- is held to the same rule. `Sprint.name` is
 * `text`, so the database enforces no length of its own.
 */
export const SPRINT_NAME_MAX_LENGTH = 60

export type SprintNameValidation =
  | { valid: true }
  | { valid: false; reason: 'empty' | 'too_long' | 'profane' | 'taken' }

export interface ValidateSprintNameOptions {
  /** Reports whether `name` is already in use by another (live) Sprint. */
  isNameTaken: IsNameTaken
}

/**
 * The gate an admin rename goes through before it can be applied to a
 * Sprint: same profanity + uniqueness gate used at generation time (see
 * {@link checkCandidate}), plus the two bounds a free-text rename needs and a
 * generated name never can -- non-empty and {@link SPRINT_NAME_MAX_LENGTH}.
 */
export async function validateSprintName(
  name: string,
  { isNameTaken }: ValidateSprintNameOptions,
): Promise<SprintNameValidation> {
  const trimmed = name.trim()
  if (trimmed.length === 0) {
    return { valid: false, reason: 'empty' }
  }
  if (trimmed.length > SPRINT_NAME_MAX_LENGTH) {
    return { valid: false, reason: 'too_long' }
  }

  const check = await checkCandidate(trimmed, isNameTaken)
  return check.ok ? { valid: true } : { valid: false, reason: check.reason }
}
